import React, { useEffect, useState } from 'react';
import { Player } from '@lottiefiles/react-lottie-player';
import {
  Box,
  Button,
  IconButton,
  Modal,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { formatCurrency } from '@utils/common';
import { FullPageLoader, CustomDropdown, GenericTable } from '@components';
import {
  INVESTMENT_STATUS_VALUES,
  useLazyGetInvestmentRequestByGameIdQuery,
  useLazyGetInvestmentRequestGameFiltersQuery,
} from '@api/investments';
import FundingDetailsActions from '../components/FundingDetails/FundingDetailsActions';
import FundingRequestSidePanel from '../components/FundingDetails/FundingRequestSidePanel';
import loader from '../../../assets/images/Loader.json';
import { PaginationConfig } from '@utils/types/PaginationConfig';
import Pagination from '../components/Pagination';

const metadata = {
  chartType: 'TABLE',
  columns: [
    {
      id: 'settlementDate',
      name: 'Settlement Date',
      type: 'string',
    },
    {
      id: 'cohort',
      name: 'Cohort',
      type: 'string',
    },
    {
      id: 'expectedSpend',
      name: 'Growth Spend',
      type: 'currency',
      currencyFormat: {
        currency: 'dollar',
        scale: 2,
      },
    },
    {
      id: 'requestedFund',
      name: 'Investment Amt.',
      type: 'currency',
      currencyFormat: {
        currency: 'dollar',
        scale: 2,
      },
    },
    {
      id: 'adjGrowthSpend',
      name: 'Adj. Growth Spend',
      type: 'currency',
      currencyFormat: {
        currency: 'dollar',
        scale: 2,
      },
    },
    {
      id: 'newInvAmount',
      name: 'New Inv. Amount',
      type: 'currency',
      currencyFormat: {
        currency: 'dollar',
        scale: 2,
      },
    },
    {
      id: 'fundingPercentage',
      name: 'Funding %',
      type: 'percentage',
    },
    {
      id: 'allocatedFund',
      name: 'Allocated Amount',
      type: 'currency',
      currencyFormat: {
        currency: 'dollar',
        scale: 2,
      },
    },
    {
      id: 'allocationType',
      name: 'Request Type',
      type: 'string',
    },
    {
      id: 'status',
      name: 'Status',
      type: 'string',
    },
  ],
};

const mappedColumns = [
  {
    key: 'settlementDate',
    label: 'Settlement Date',
  },
  {
    key: 'cohort',
    label: 'Cohort',
  },
  {
    key: 'expectedSpend',
    label: 'Growth Spend',
  },
  {
    key: 'requestedFund',
    label: 'Investment Amt.',
  },
  {
    key: 'adjGrowthSpend',
    label: 'Adj. Growth Spend',
  },
  {
    key: 'newInvAmount',
    label: 'New Inv. Amount',
  },
  {
    key: 'fundingPercentage',
    label: 'Funding %',
  },
  {
    key: 'allocatedFund',
    label: 'Allocated Amount',
  },
  {
    key: 'allocationType',
    label: 'Request Type',
  },
  {
    key: 'status',
    label: 'Status',
  },
];

const PAGE_SIZE = 25;

const FundingDetails: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [dashboardLoader, setDashboardLoader] = useState<boolean>(true);
  const [showFundingRequestPanel, setShowFundingRequestPanel] = useState(false);
  const [fundingRequestDetails, setFundingRequestDetails] = useState(null);
  const [gamesList, setGamesList] = useState<{ id: number; label: string; value: string }[]>([]);
  const [selectedGame, setSelectedGame] = useState<{
    id: number;
    label: string;
    value: string;
  }>({ id: -1, label: '', value: '' });
  const [investmentRequests, setInvestmentRequests] = useState([]);
  const [renderTable, setRenderTable] = useState(false);
  const [fundingRequestsPagination, setFundingRequestsPagination] = useState<PaginationConfig>({
    pageNumber: 1,
    pageSize: PAGE_SIZE,
    total: 0,
  });

  const [getInvestmentRequestsGameFilters] = useLazyGetInvestmentRequestGameFiltersQuery();
  const [getInvestmentRequestByGameId, { isLoading: isInvestmentRequestLoading }] =
    useLazyGetInvestmentRequestByGameIdQuery();

  const [showModal, setShowModal] = useState<{
    allowedFundingCohort: string;
    signingAuthority: string;
    signingAuthorityEmail: string;
  } | null>(null);

  // Hook to fetch games on page load
  useEffect(() => {
    const fetchGames = async () => {
      const res = await getInvestmentRequestsGameFilters({}).unwrap();
      if (res.data.length) {
        const gamesList = res.data.map(
          (game: { gameId: string; gameName: string; dealName: string }, index: number) => ({
            id: index + 1,
            label: game.dealName ? `${game.dealName} - ${game.gameId}` : `${game.gameId}`,
            value: game.gameId,
          }),
        );
        setGamesList(gamesList);
        setSelectedGame({
          id: 1,
          label: gamesList[0].label,
          value: gamesList[0].value,
        });
      }
    };

    if (!gamesList.length) {
      fetchGames();
    }
  }, []);

  // Hook to fetch investment requests for seleted game
  useEffect(() => {
    if (selectedGame.value) {
      fetchInvestmentRequests(selectedGame.value);
    }
    if (!showFundingRequestPanel) {
      setFundingRequestDetails(null);
    }
  }, [selectedGame.value, showFundingRequestPanel]);

  useEffect(() => {
    setRenderTable(!renderTable);
  }, [showFundingRequestPanel]);

  const fetchInvestmentRequests = async (selectedGame: string, pageNumber: number = 1) => {
    pageNumber = pageNumber - 1;
    const queryParams = `?sort=cohort&sortOrder=DESC&pageNumber=${pageNumber}&pageSize=${fundingRequestsPagination.pageSize}`;

    const res = await getInvestmentRequestByGameId({
      gameId: selectedGame,
      queryParams,
    }).unwrap();

    setFundingRequestsPagination({
      ...fundingRequestsPagination,
      pageNumber: pageNumber + 1,
      total: res.data.totalElements,
    });

    setDashboardLoader(false);
    setInvestmentRequests(res?.data?.items || []);
  };

  const getRequestTypeIndicatorColor = (row: any) => {
    return row.fundingAdjustmentReported
      ? Number(row.allocatedAdjustment) > 0
        ? '#601410'
        : '#FFA726'
      : '#2E736E';
  };

  const conditionallyMappedColumns = mappedColumns.map((col: any) => ({
    ...col,
    ...(col.key === 'settlementDate' && {
      render: (key: string, row: any) => {
        const val = row.adjustmentSettlementDate || row.settlementDate;

        return (
          <Box
            sx={{
              width: '100%',
              textAlign: 'left',
            }}
          >
            <Typography fontSize={'0.875rem'} fontWeight={550}>
              {`${val}`}
            </Typography>
          </Box>
        );
      },
    }),
    ...(col.key === 'expectedSpend' && {
      render: (key: string, row: any) => {
        const val = row.expectedSpend || 0;

        return (
          <Box
            sx={{
              width: '100%',
              textAlign: 'right',
            }}
          >
            <Typography fontSize={'0.875rem'} fontWeight={550}>
              {`${val ? formatCurrency(val) : '-'}`}
            </Typography>
          </Box>
        );
      },
    }),
    ...(col.key === 'requestedFund' && {
      render: (key: string, row: any) => {
        const val = row.requestedFund || 0;

        return (
          <Box
            sx={{
              width: '100%',
              textAlign: 'right',
            }}
          >
            <Typography fontSize={'0.875rem'} fontWeight={550}>
              {`${val ? formatCurrency(val) : '-'}`}
            </Typography>
          </Box>
        );
      },
    }),
    ...(col.key === 'adjGrowthSpend' && {
      render: (key: string, row: any) => {
        const val = row.actualSpend || 0;

        return (
          <Box
            sx={{
              width: '100%',
              textAlign: 'right',
            }}
          >
            <Typography fontSize={'0.875rem'} fontWeight={550}>
              {`${val ? formatCurrency(val) : '-'}`}
            </Typography>
          </Box>
        );
      },
    }),
    ...(col.key === 'newInvAmount' && {
      render: (key: string, row: any) => {
        const val =
          (row?.requestedAdjustment ? row.allocatedFund + row.requestedAdjustment : 0) || 0;

        return (
          <Box
            sx={{
              width: '100%',
              textAlign: 'right',
            }}
          >
            <Typography fontSize={'0.875rem'} fontWeight={550}>
              {`${val ? formatCurrency(val) : '-'}`}
            </Typography>
          </Box>
        );
      },
    }),
    ...(col.key === 'fundingPercentage' && {
      render: (key: string, row: any) => {
        const val = row.fundingPercentage;

        return (
          <Box
            sx={{
              width: '100%',
              textAlign: 'right',
            }}
          >
            <Typography fontSize={'0.875rem'} fontWeight={550}>
              {val ? `${Number(val * 100).toFixed(3)}%` : '-'}
            </Typography>
          </Box>
        );
      },
    }),
    ...(col.key === 'allocatedFund' && {
      render: (key: string, row: any) => {
        const val =
          (row?.allocatedAdjustment
            ? row.allocatedFund + row.allocatedAdjustment
            : row.allocatedFund) || 0;
        return (
          <Box
            sx={{
              width: '100%',
              textAlign: 'right',
            }}
          >
            <Typography fontSize={'0.875rem'} fontWeight={550}>
              {`${val ? formatCurrency(val) : '-'}`}
            </Typography>
          </Box>
        );
      },
    }),
    ...(col.key === 'allocationType' && {
      render: (key: string, row: any) => {
        return (
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              textAlign: 'left',
            }}
          >
            <Box
              sx={{
                height: '0.85rem',
                width: '0.85rem',
                borderRadius: '0.25rem',
                background: getRequestTypeIndicatorColor(row),
                m: '0.5rem',
              }}
            />
            <Typography fontSize={'0.875rem'} fontWeight={550}>
              {row.fundingAdjustmentReported
                ? Number(row.allocatedAdjustment || row.requestedAdjustment) > 0
                  ? 'Overspend'
                  : 'Underspend'
                : 'New'}
            </Typography>
          </Box>
        );
      },
    }),
    ...(col.key === 'status' && {
      render: (key: string, row: any) => {
        return (
          <Box
            sx={{
              width: '100%',
              textAlign: 'left',
            }}
          >
            <Typography fontSize={'0.875rem'} fontWeight={550}>
              {INVESTMENT_STATUS_VALUES[row.status as keyof typeof INVESTMENT_STATUS_VALUES]}
            </Typography>
          </Box>
        );
      },
    }),
  }));

  return (
    <>
      {dashboardLoader && <FullPageLoader hasSideNav={true} />}
      <div
        id="pvx-funding-details-container"
        style={{
          width: '100%',
          height: '100vh',
          border: 'none',
          overflow: 'auto',
          paddingLeft: '2rem',
          paddingRight: '2rem',
          paddingTop: '1rem',
          paddingBottom: '1rem',
          position: 'relative',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            position: 'relative',
            mt: '0.5rem',
            mb: '1.5rem',
            flexDirection: isMobile ? 'column' : 'row',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              gap: '0.5rem',
              flexDirection: isMobile ? 'column' : 'row',
            }}
          >
            <Typography fontSize={'2rem'} fontWeight={600} sx={{ color: '#1B1C17' }}>
              Funding Details: {gamesList.length === 1 ? gamesList[0].label : ''}
            </Typography>
            {gamesList.length > 1 ? (
              <CustomDropdown
                dropdownOptions={gamesList.map((g) => ({
                  ...g,
                  label: g.label,
                }))}
                handleChange={(e) => {
                  setSelectedGame(gamesList[parseInt(e.target.value) - 1]);
                }}
                selectedOption={selectedGame?.id?.toString() || ''}
                wrapperSx={{
                  fontSize: isMobile ? '1rem' : '1.5rem',
                  color: '#1B1C17',
                  lineHeight: 'none',
                  border: '1px solid #2F736E46',
                }}
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: '15rem',
                    },
                  },
                }}
              />
            ) : (
              <></>
            )}
          </Box>
          <Box
            sx={{
              position: 'absolute',
              top: '1rem',
              right: '0rem',
            }}
          >
            <Typography
              sx={{
                fontFamily: 'Manrope, sans-serif',
                fontWeight: 700,
                fontSize: '0.875rem',
                lineHeight: '1.25rem',
                color: '#6C6C6C',
              }}
            >
              $ = USD
            </Typography>
          </Box>
        </Box>
        <FundingDetailsActions
          handleSidePanelOpen={() => {
            setShowFundingRequestPanel(true);
          }}
        />
        <Box
          sx={{
            marginTop: '1.125rem',
            backgroundColor: 'white',
            borderRadius: '0.75rem',
            padding: '0.75rem',
          }}
        >
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
              marginBottom: '1rem',
            }}
          >
            <Typography
              sx={{
                fontSize: '1rem',
                lineHeight: '1.375rem',
                fontWeight: 700,
                color: '#162936',
              }}
            >
              Funding Requests
            </Typography>
            <Stack direction={'row'} gap={'0.5rem'}>
              <Pagination
                page={fundingRequestsPagination.pageNumber}
                size={fundingRequestsPagination.pageSize}
                total={fundingRequestsPagination.total}
                handleChange={(pageNumber) =>
                  fetchInvestmentRequests(selectedGame.value, pageNumber)
                }
                fileName={''}
                chartId={''}
                submoduleId={''}
                filterSelection={{}}
              />

              <IconButton
                onClick={(e: any) => fetchInvestmentRequests(selectedGame.value)}
                sx={{ color: '#2E736E' }}
              >
                <RefreshIcon />
              </IconButton>
            </Stack>
          </Box>
          <GenericTable
            data={investmentRequests}
            columns={conditionallyMappedColumns}
            meta={metadata}
            actions={[
              {
                icon: 'table-row-next.svg',
                action: (id) => {
                  setFundingRequestDetails(
                    investmentRequests.find((r: any) => r.id === id) || null,
                  );
                  setShowFundingRequestPanel(true);
                },
              },
            ]}
            onViewRow={(row: any) => {
              setFundingRequestDetails(row);
              setShowFundingRequestPanel(true);
            }}
            isLoading={isInvestmentRequestLoading}
          />
        </Box>
        <FundingRequestSidePanel
          showFundingRequestPanel={showFundingRequestPanel}
          setShowFundingRequestPanel={setShowFundingRequestPanel}
          fundingRequestId={(fundingRequestDetails as any)?.id || ''}
          gameId={selectedGame.value}
          fundingRequestDetails={fundingRequestDetails}
          setShowModal={setShowModal}
        />
      </div>
      <Modal
        open={Boolean(showModal?.signingAuthority) && Boolean(showModal?.signingAuthorityEmail)}
        onClose={() => setShowModal(null)}
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 400,
            bgcolor: 'background.paper',
            borderRadius: '28px',
            padding: '1.5rem',
          }}
        >
          <Typography
            sx={{
              fontFamily: 'Manrope, sans-serif',
              fontWeight: 700,
              fontSize: '1rem',
              lineHeight: '1.375rem',
              color: '#162936',
              marginBottom: '1rem',
            }}
          >
            You will shortly receive a signature request via Docusign from info@pvxpartners.com
          </Typography>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              width: '100%',
              py: '0.5rem',
            }}
          >
            <Player
              src={loader}
              className="player"
              loop
              autoplay
              style={{
                width: '5rem',
              }}
            />
          </Box>
          <Typography
            sx={{
              fontFamily: 'Manrope, sans-serif',
              fontWeight: 500,
              fontSize: '0.875rem',
              lineHeight: '1.25rem',
              color: '#6C6C6C',
            }}
          >
            Signing authority: {showModal?.signingAuthority}
          </Typography>
          <Typography
            sx={{
              fontFamily: 'Manrope, sans-serif',
              fontWeight: 500,
              fontSize: '0.875rem',
              lineHeight: '1.25rem',
              color: '#6C6C6C',
            }}
          >
            Signing authority email: {showModal?.signingAuthorityEmail}
          </Typography>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
            }}
          >
            <Button
              variant="contained"
              color="primary"
              sx={{
                mt: 3,
                borderRadius: '2rem',
                textTransform: 'none',
              }}
              onClick={() => setShowModal(null)}
            >
              Okay
            </Button>
          </Box>
        </Box>
      </Modal>
    </>
  );
};

export default FundingDetails;
